// =============================================================================
// RENTABILITE GAUGE - Design Minimaliste v2 + Switch Temps réel / Finale
// =============================================================================

import { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Info, Clock, Target } from 'lucide-react';
import { RentabiliteData } from '@/hooks/useChartData';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  CHART_STATUS_COLORS,
  TOOLTIP_EXPLANATIONS,
} from './chartConfig';

interface Props {
  data: RentabiliteData;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function RentabiliteGauge({ data }: Props) {
  const { value, valueFinale, objectif, color, colorFinale, status, statusFinale, hasActivity } = data;

  // Switch entre temps réel et finale estimée
  const [showFinale, setShowFinale] = useState(false);

  // Valeurs actives selon le mode
  const activeValue = showFinale ? valueFinale : value;
  const activeColor = showFinale ? colorFinale : color;
  const activeStatus = showFinale ? statusFinale : status;

  // État "En attente" : chantier sans activité et valeur à 0
  if (!hasActivity && value === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4">
        {/* Valeur placeholder */}
        <div className="text-center mb-6">
          <span className="text-5xl font-bold text-muted-foreground/50">—</span>
          <p className="text-lg text-muted-foreground mt-2">En attente</p>
        </div>

        {/* Barre vide */}
        <div className="w-full max-w-[320px] mb-6">
          <div className="h-3 rounded-full bg-muted/30" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground/50">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          La rentabilité sera calculée après les premières dépenses ou affectations d'équipe.
        </p>

        {/* Info */}
        <div className="mt-6 flex items-start gap-2 max-w-[320px] px-3 py-2 rounded-lg bg-primary/5 border border-primary/10">
          <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {TOOLTIP_EXPLANATIONS.rentabilite}
          </p>
        </div>
      </div>
    );
  }

  // Calculs pour la barre de progression
  const progressData = useMemo(() => {
    // Normaliser entre 0 et 100 pour l'affichage
    const normalizedValue = Math.min(Math.max(activeValue, 0), 100);
    const objectifPosition = Math.min(objectif, 100);

    return {
      fillPercent: normalizedValue,
      objectifPosition,
      isAboveObjectif: activeValue >= objectif,
      ecart: Math.abs(activeValue - objectif).toFixed(1),
    };
  }, [activeValue, objectif]);

  // Icône selon la tendance
  const TrendIcon = activeValue >= objectif ? TrendingUp : TrendingDown;
  const trendColor = activeValue >= objectif ? 'text-emerald-400' : 'text-amber-400';

  // Icône statut objectif
  const ObjectifIcon = progressData.isAboveObjectif ? CheckCircle2 : AlertTriangle;
  const objectifIconColor = progressData.isAboveObjectif ? 'text-emerald-400' : 'text-amber-400';

  return (
    <TooltipProvider delayDuration={200}>
    <div className="h-full flex flex-col items-center justify-center px-4">
      {/* Switch Temps réel / Finale estimée */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(
              "flex items-center gap-1.5 text-sm font-medium transition-colors cursor-help",
              !showFinale ? "text-emerald-400" : "text-muted-foreground"
            )}>
              <Clock className="w-4 h-4" />
              <span>Temps réel</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-sm">
              <strong>Rentabilité à ce jour</strong><br/>
              Calculée sur les coûts réels engagés jusqu'à aujourd'hui.
              Se met à jour automatiquement avec les retards, suspensions et reprises.
            </p>
          </TooltipContent>
        </Tooltip>

        <Switch
          checked={showFinale}
          onCheckedChange={setShowFinale}
          className="data-[state=checked]:bg-amber-500"
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(
              "flex items-center gap-1.5 text-sm font-medium transition-colors cursor-help",
              showFinale ? "text-amber-400" : "text-muted-foreground"
            )}>
              <Target className="w-4 h-4" />
              <span>Finale estimée</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-sm">
              <strong>Rentabilité finale projetée</strong><br/>
              Estimation basée sur la durée restante et les coûts prévisionnels.
              Intègre les retards, suspensions et coûts d'équipe planifiés.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Pourcentage principal - HERO */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-6xl font-bold tracking-tight"
            style={{ color: activeColor }}
          >
            {activeValue.toFixed(1)}%
          </span>
          <TrendIcon className={`h-8 w-8 ${trendColor}`} />
        </div>
        <p
          className="text-xl font-medium mt-2"
          style={{ color: activeColor }}
        >
          {activeStatus}
        </p>
      </div>

      {/* Barre de progression minimaliste */}
      <div className="w-full max-w-[320px] mb-8">
        {/* Barre */}
        <div className="relative h-3 rounded-full bg-muted/30 overflow-hidden">
          {/* Remplissage avec dégradé */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${progressData.fillPercent}%`,
              background: `linear-gradient(90deg, ${CHART_STATUS_COLORS.attention} 0%, ${activeColor} 100%)`,
              boxShadow: `0 0 12px ${activeColor}60`,
            }}
          />

          {/* Marqueur objectif */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-slate-300"
            style={{ left: `${progressData.objectifPosition}%` }}
          />
        </div>

        {/* Légende */}
        <div className="flex justify-between mt-2">
          <span className="text-xs text-muted-foreground">0%</span>
          <span className="text-xs text-muted-foreground">
            <span className="text-slate-300">│</span> {objectif}% objectif
          </span>
          <span className="text-xs text-muted-foreground">100%</span>
        </div>
      </div>

      {/* Carte objectif */}
      <div className={`
        w-full max-w-[320px] p-4 rounded-xl border
        ${progressData.isAboveObjectif
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-amber-500/10 border-amber-500/30'
        }
      `}>
        <div className="flex items-center gap-3">
          <ObjectifIcon className={`h-6 w-6 ${objectifIconColor}`} />
          <div>
            <p className="font-medium text-foreground">
              Objectif BTP : {objectif}%
            </p>
            <p className={`text-sm ${objectifIconColor}`}>
              {progressData.isAboveObjectif
                ? `Dépassé de +${progressData.ecart} points`
                : `${progressData.ecart} points restants`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Indicateur temps réel */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span>Données mises à jour en temps réel</span>
      </div>
    </div>
    </TooltipProvider>
  );
}
